import React from 'react';
import { TouchableOpacity } from 'react-native';

import { Box } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';

/**
 * TA-10279: per-file upload progress indicator (global rule: "use a
 * per-file progress indicator for file uploads, not a single button
 * spinner"). Extracted from `ChatImages.tsx`, which already implemented
 * this correctly for image/video attachments — this makes it reusable for
 * other attachment types (e.g. `DocumentCard.web.tsx`) rather than having
 * two divergent implementations.
 *
 * Callers own all the condition logic (which state to show, when); this
 * component only renders.
 */
interface UploadProgressOverlayProps {
  showProgressBar: boolean;
  /** 0..1 combined compression+upload progress. */
  progress: number;
  showSuccessIcon: boolean;
  showErrorRetry: boolean;
  onRetry?: () => void;
}

export const UploadProgressOverlay: React.FC<UploadProgressOverlayProps> = ({
  showProgressBar,
  progress,
  showSuccessIcon,
  showErrorRetry,
  onRetry,
}) => {
  return (
    <>
      {showProgressBar && (
        <Box
          position='absolute'
          bottom={0}
          left={0}
          right={0}
          paddingHorizontal='xxs'
          paddingVertical='xxxs'
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
          borderBottomLeftRadius='xs'
          borderBottomRightRadius='xs'>
          <Box
            height={3}
            backgroundColor='white'
            borderRadius='xs'
            overflow='hidden'>
            <Box
              backgroundColor='greenSecondary'
              width={`${Math.floor(progress * 100)}%`}
              height='100%'
            />
          </Box>
        </Box>
      )}

      {showSuccessIcon && (
        <Box
          position='absolute'
          bottom={4}
          right={4}
          backgroundColor='greenSecondary'
          padding='xxxs'
          borderRadius='xs'>
          <Icon name='Check' color='white' variant='xxxs' />
        </Box>
      )}

      {showErrorRetry && (
        <Box
          position='absolute'
          bottom={4}
          right={4}
          backgroundColor='alertRed'
          padding='xxs'
          borderRadius='xs'>
          <TouchableOpacity onPress={onRetry} accessibilityLabel='Retry upload'>
            <Icon name='RotateCw' color='white' variant='xxxs' />
          </TouchableOpacity>
        </Box>
      )}
    </>
  );
};
